/**
 * Contextual actions for the current selection (the selected-unit chip and its hotkeys).
 * Hotkeys never collide with camera keys (WASD) or the build strip (1–7).
 */
import { isFriendlyTo } from '@sa/protocol';
import { BALANCE, TICK_HZ, cityUpgradeCost, missilePos, samRange, type EntityId, type EntityView, type Intent, type MissileTrack, type PlayerView } from '@sa/sim';
import type { CityIncome } from '../state/economy';
import { fmtInt } from '../ui/dom';
import { parkedAt, readyLaunchers } from './commands';
import type { AimAction } from './controller';

export interface ChipAction {
  key: string;
  label: string;
  cost?: number;
  enabled: boolean;
  /** why it is disabled (shown on the alert line when pressed anyway) */
  why?: string;
  /** it already took effect (e.g. BRACED 0:14): shown as a status, not a button */
  done?: boolean;
  /** a small figure after the price (e.g. an upgrade's `+50/MIN`) */
  note?: string;
  /** shown above the button on hover (e.g. the upgrade's payback) */
  tip?: string;
  /** a done action still under way: how far along (0..1), drawn as a thin bar (UPGRADING → L4 · 0:21) */
  progress?: number;
  /** intents to send (one per selected unit), or an aim mode to enter, or something to run */
  intent?: Intent | Intent[];
  aim?: AimAction;
  run?: () => void;
}

export interface Chip {
  title: string;
  stats: string;
  actions: ChipAction[];
}

/**
 * The hotkey grid: one key per kind of order, the same on every unit (RTS style), around WASD.
 *
 *   Q ATTACK (launch, strike)   E DEFEND (SAM target, patrol)   R SPECIAL (scout, brace)
 *   F BUY (missile, interceptor, fighter, upgrade)              G BUY #2 (bomber)
 *   Z TOGGLE (auto-scramble)    X CANCEL (cancel, clear, recall, stop ripple)
 *
 * With nothing selected the top row does the same globally: Q strikes a rival's critical capital,
 * E focuses our SAMs on the next threat, R braces the threatened city.
 */
export const KEY = { attack: 'q', defend: 'e', special: 'r', buy: 'f', buy2: 'g', toggle: 'z', cancel: 'x' } as const;
const K = (k: string) => k.toUpperCase();

const KIND_NAME: Record<string, string> = {
  radar: 'RADAR',
  sam: 'SAM SITE',
  silo: 'SILO',
  city: 'CITY',
  airbase: 'AIRBASE',
  sub: 'SUBMARINE',
  carrier: 'CARRIER',
  fighter: 'FIGHTER',
  bomber: 'BOMBER',
};

const secs = (ticks: number) => Math.max(0, Math.ceil(ticks / TICK_HZ));
const mmss = (ticks: number) => {
  const s = secs(ticks);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const broke = (view: PlayerView, cost: number) => (view.credits >= cost ? undefined : `NOT ENOUGH CREDITS · NEED ${fmtInt(cost - view.credits)} MORE`);

/**
 * Brace (§1.15) for one of our cities: R. Disabled while braced (label counts down), during the
 * cooldown (label counts down to ready) or when we cannot pay.
 */
export function braceAction(view: PlayerView, city: EntityView): ChipAction {
  const d = city.detail ?? {};
  const t = view.tick;
  const cost = BALANCE.brace.cost;
  if ((d.braceUntil ?? 0) > t) return { key: KEY.special, label: `BRACED ${mmss(d.braceUntil! - t)}`, enabled: false, done: true, why: 'ALREADY BRACED' };
  if ((d.braceReadyTick ?? 0) > t) return { key: KEY.special, label: `BRACE ${mmss(d.braceReadyTick! - t)}`, enabled: false, why: `BRACE READY IN ${mmss(d.braceReadyTick! - t)}` };
  return { key: KEY.special, label: 'BRACE', cost, enabled: view.credits >= cost, why: broke(view, cost), intent: { type: 'brace', cityId: city.id } };
}

/** Points sampled along a missile's remaining flight when testing whether a SAM can reach it. */
const REACH_SAMPLES = 16;

/**
 * Hostile missiles a SAM can engage before they land (some point of the remaining flight is
 * within its range), earliest impact first — the candidates for a SAM priority (§1.15).
 */
export function samTargets(view: PlayerView, sam: EntityView, tick = view.tick): MissileTrack[] {
  const r2 = samRange(!!sam.detail?.silent) ** 2;
  const p = { x: 0, y: 0 };
  const out: MissileTrack[] = [];
  for (const t of view.tracks.values()) {
    if (isFriendlyTo(view, t.owner) || (t.kind !== 'icbm' && t.kind !== 'slbm')) continue;
    const impact = t.launchTick + t.flightTicks;
    if (impact <= tick) continue;
    const from = Math.max(tick, t.launchTick);
    for (let i = 0; i <= REACH_SAMPLES; i++) {
      missilePos(t, from + ((impact - from) * i) / REACH_SAMPLES, p);
      if ((p.x - sam.x) ** 2 + (p.y - sam.y) ** 2 <= r2) {
        out.push(t);
        break;
      }
    }
  }
  return out.sort((a, b) => a.launchTick + a.flightTicks - (b.launchTick + b.flightTicks) || a.id - b.id);
}

/** Our finished SAM sites with ammo that can still reach missile `trackId` before it lands. */
export function samsReaching(view: PlayerView, trackId: EntityId, tick = view.tick): EntityView[] {
  const out: EntityView[] = [];
  for (const e of view.entities.values()) {
    if (e.kind !== 'sam' || e.owner !== view.me || e.state === 'building' || (e.detail?.ammo ?? e.extra) <= 0) continue;
    if (samTargets(view, e, tick).some((t) => t.id === trackId)) out.push(e);
  }
  return out;
}

/** SAM focus without a selection (E): every SAM of ours that can reach `trackId` engages it first. */
export function samFocusIntents(view: PlayerView, trackId: EntityId, tick = view.tick): Intent[] {
  return samsReaching(view, trackId, tick).map((s) => ({ type: 'samPriority', samId: s.id, targetId: trackId }));
}

/** The SAM's priority target if we still track it. */
export function samPriority(view: PlayerView, sam: EntityView): MissileTrack | null {
  const id = sam.detail?.priorityId ?? 0;
  return id ? (view.tracks.get(id) ?? null) : null;
}

/** Priority intents for the selected SAMs: the next reachable missile after the current one. */
function nextPriority(view: PlayerView, sams: readonly EntityView[]): { intents: Intent[]; any: boolean } {
  const intents: Intent[] = [];
  for (const s of sams) {
    const list = samTargets(view, s);
    if (!list.length) continue;
    const cur = list.findIndex((t) => t.id === s.detail?.priorityId);
    const next: EntityId = list[(cur + 1) % list.length].id;
    intents.push({ type: 'samPriority', samId: s.id, targetId: next });
  }
  return { intents, any: intents.length > 0 };
}

/** Why none of `launchers` can fire right now (null if one can). */
export function launchBlocker(view: PlayerView, launchers: readonly EntityView[]): string | null {
  if (readyLaunchers(view, launchers).length) return null;
  const t = view.tick;
  if (!view.released) return `WEAPONS LOCKED · ${mmss(BALANCE.phases.releaseTick - t)}`;
  const own = launchers.filter((e) => e.owner === view.me && (e.kind === 'silo' || e.kind === 'sub'));
  const loaded = own.filter((e) => (e.detail?.loaded ?? 0) > 0);
  if (loaded.length) {
    const next = Math.min(...loaded.map((e) => e.detail?.cooldownUntil ?? t));
    return `RELOADING · ${mmss(next - t)}`;
  }
  const loading = own.filter((e) => (e.detail?.queued ?? 0) > 0 && e.detail?.loadDoneTick);
  const inPort = loading.filter(atPort);
  if (inPort.length) return `LOADING · ${mmss(Math.min(...inPort.map((e) => e.detail!.loadDoneTick!)) - t)}`;
  if (loading.length) return 'SLBMS LOAD IN PORT · SAIL TO OUR COAST';
  const word = own[0]?.kind === 'sub' ? 'SLBM' : 'ICBM';
  return own.some(atPort) ? `NO ${word} · ${K(KEY.buy)} TO BUY` : 'NO SLBM · LOAD AT OUR COAST';
}

/** A silo, or a sub at rest on our coast (where SLBMs are bought and load, §1.7). */
const atPort = (e: EntityView): boolean => e.kind !== 'sub' || e.detail?.docked !== false;

/**
 * The submarine chip: `SLBM 1/2`, then what matters most right now (exposed after a launch,
 * loading, or waiting for a port), Q to aim and F to buy — also while it is still being built (it
 * lies at its port, so it can load before it is done). Its reach never changes: the ring shows it.
 */
function subChip(view: PlayerView, own: readonly EntityView[], e: EntityView): { stats: string; actions: ChipAction[] } {
  const d = e.detail ?? {};
  const t = view.tick;
  const loaded = d.loaded ?? 0;
  const queued = d.queued ?? 0;
  const port = atPort(e);
  let stats = `SLBM ${loaded}/${BALANCE.sub.slbms}`;
  if (e.state === 'building') stats += d.buildDoneTick !== undefined ? ` · BUILDING ${mmss(d.buildDoneTick - t)}` : ' · BUILDING';
  else if ((d.exposedUntil ?? 0) > t) stats += ` · EXPOSED ${mmss(d.exposedUntil! - t)}`;
  if (queued && d.loadDoneTick) stats += port ? ` · LOAD ${mmss(d.loadDoneTick - t)}${queued > 1 ? ` +${queued - 1}` : ''}` : ' · LOADS IN PORT';
  // torpedoes fire by themselves; only spent ones are news (they restock in port)
  if ((d.torps ?? BALANCE.sub.torps) < BALANCE.sub.torps) stats += ` · TORP ${d.torps}/${BALANCE.sub.torps}`;
  const actions: ChipAction[] = [];
  if (e.state !== 'building') {
    const block = launchBlocker(view, own);
    actions.push({ key: KEY.attack, label: view.released ? 'LAUNCH' : 'LOCKED', enabled: block === null, why: block ?? undefined, aim: 'launch' });
  }
  // F: one SLBM into each selected sub with room in port (several subs selected: ×n)
  const subs = own.filter((x) => x.kind === 'sub');
  actions.push(buyMissiles(view, subs.length ? subs : [e]));
  if (e.state === 'building') actions.push({ key: KEY.cancel, label: 'CANCEL', enabled: true, intent: { type: 'cancel', unitId: e.id } });
  return { stats, actions };
}

interface HangarKind {
  /** parked, armed, ready to fly */
  ready: number;
  /** all of this kind based here (building, airborne, rearming included) */
  total: number;
  /** soonest building / rearming one ready at (null: none on the way) */
  next: number | null;
  /** a bomber waiting for its rearm to be paid */
  unpaid: boolean;
}

/** What an airbase holds, per kind, and when the next aircraft is ready. */
export function hangarOf(view: PlayerView, baseId: EntityId): { fighter: HangarKind; bomber: HangarKind; count: number; nextReady: number | null } {
  const mk = (): HangarKind => ({ ready: 0, total: 0, next: null, unpaid: false });
  const out = { fighter: mk(), bomber: mk(), count: 0, nextReady: null as number | null };
  for (const a of view.entities.values()) {
    if (a.owner !== view.me || (a.kind !== 'fighter' && a.kind !== 'bomber') || a.detail?.baseId !== baseId) continue;
    const k = out[a.kind];
    const d = a.detail;
    k.total++;
    out.count++;
    if (d.phase === 'parked' && (a.kind !== 'bomber' || (d.ammo ?? 1) > 0)) k.ready++;
    else if (d.phase === 'building' || d.phase === 'rearming') {
      if (d.readyTick && d.readyTick > view.tick) k.next = Math.min(k.next ?? Infinity, d.readyTick);
      else if (d.phase === 'rearming') k.unpaid = true;
    }
  }
  const nexts = [out.fighter.next, out.bomber.next].filter((x): x is number => x !== null);
  out.nextReady = nexts.length ? Math.min(...nexts) : null;
  return out;
}

/** Why no aircraft of a kind can fly: on the way, waiting for its rearm, out, or none bought. */
function noneReady(k: HangarKind, name: string, key: string, t: number): string {
  if (k.next !== null) return `${name} READY IN ${mmss(k.next - t)}`;
  if (k.unpaid) return `${name} REARM · NEED ${fmtInt(BALANCE.bomber.rearmCost)}`;
  if (k.total > 0) return `NO ${name} AT BASE · ALL ${k.total} OUT`;
  return `NO ${name} · ${K(key)} TO BUILD`;
}

/** An aircraft's chip line: what it is doing and for how long (ETA to its target / base). */
function aircraftStats(view: PlayerView, e: EntityView): string {
  const d = e.detail ?? {};
  const t = view.tick;
  const speed = BALANCE[e.kind as 'fighter' | 'bomber'].speed;
  const base = d.baseId ? view.entities.get(d.baseId) : undefined;
  // (a mobile unit's x, y is where its motion segment started)
  const dt = (t - e.segTick) / TICK_HZ;
  const eta = (x: number, y: number) => mmss((Math.hypot(x - (e.x + e.vx * dt), y - (e.y + e.vy * dt)) / speed) * TICK_HZ);
  const ammo = e.kind === 'bomber' ? `BOMB ${d.ammo ?? 0}` : `AAM ${d.ammo ?? 0}`;
  switch (d.phase) {
    case 'outbound':
      return `${d.mission === 'strike' ? 'STRIKE · DROP IN' : d.mission === 'patrol' ? 'PATROL · ON STATION IN' : 'SCOUT · OVER TARGET IN'} ${eta(d.tx ?? e.x, d.ty ?? e.y)} · ${ammo}`;
    case 'onStation':
      return `ON STATION ${mmss((d.stationUntil ?? t) - t)} · ${ammo}`;
    case 'intercept':
      return `INTERCEPTING · ${ammo}`;
    case 'returning':
      return `RETURNING${base ? ` ${eta(base.x, base.y)}` : ''} · ${ammo}`;
    case 'rearming':
      return d.readyTick && d.readyTick > t ? `REARMING ${mmss(d.readyTick - t)}` : `REARM ${fmtInt(BALANCE.bomber.rearmCost)} · WAITING FOR CREDITS`;
    case 'building':
      return d.readyTick ? `BUILDING · ${mmss(d.readyTick - t)}` : 'BUILDING';
    default:
      return `READY · ${ammo}`;
  }
}

/** `+180/MIN` */
export const perMin = (n: number) => `+${fmtInt(n)}/MIN`;

/** The upgrade's hover line: how soon one more level pays for itself (or that it buys a hit, not credits). */
export function paybackTip(c: CityIncome, ticksLeft: number): string | undefined {
  if (c.paybackMin === null) return undefined;
  const left = ticksLeft / TICK_HZ / 60;
  if (c.paybackMin > left) return 'WILL NOT PAY BACK BEFORE THE END · +1 LEVEL = ONE MORE HIT';
  return `PAYS BACK IN ~${Math.max(1, Math.round(c.paybackMin))} MIN`;
}

/** Client-side order state the chip shows (not in the view). */
export interface ChipState {
  /** cities whose brace is armed (G pressed early) */
  braceArms?: ReadonlySet<EntityId>;
  /** launchers rippling (Q twice: firing until their magazines are empty), and how to stop them */
  rippling?: ReadonlySet<EntityId>;
  stopRipple?: (ids: EntityId[]) => void;
}

/**
 * Silos (one or several selected), as one: `ICBM 5/8 · LOADING 0:06 +2` (the next load of any of
 * them, and how many more are queued) or `RELOAD 0:02` while every loaded one is cooling down.
 */
export function silosStats(view: PlayerView, silos: readonly EntityView[]): string {
  const t = view.tick;
  const sum = (f: (d: NonNullable<EntityView['detail']>) => number | undefined) => silos.reduce((n, s) => n + (f(s.detail ?? {}) ?? 0), 0);
  const loaded = sum((d) => d.loaded);
  const queued = sum((d) => d.queued);
  let stats = `ICBM ${loaded}/${BALANCE.silo.magazine * silos.length}`;
  const loading = silos.filter((s) => (s.detail?.queued ?? 0) > 0 && s.detail?.loadDoneTick);
  if (loading.length) {
    const next = Math.min(...loading.map((s) => s.detail!.loadDoneTick!));
    stats += ` · LOADING ${mmss(next - t)}${queued > 1 ? ` +${queued - 1}` : ''}`;
  } else if (loaded > 0 && !readyLaunchers(view, silos).length) {
    const cooling = silos.filter((s) => (s.detail?.loaded ?? 0) > 0).map((s) => s.detail?.cooldownUntil ?? t);
    if (Math.min(...cooling) > t) stats += ` · RELOAD ${mmss(Math.min(...cooling) - t)}`;
  }
  return stats;
}

/**
 * F with launchers selected: one missile into EACH that has room (a sub only in port), emptiest
 * first, as many as the credits cover — `BUY ICBM ×3` at 3× the price (one launcher: `BUY ICBM`).
 * Disabled with the reason when none has room (`MAGAZINE FULL`, a sub away from port) or the
 * credits do not cover even one.
 */
export function buyMissiles(view: PlayerView, launchers: readonly EntityView[]): ChipAction {
  const sub = launchers[0]?.kind === 'sub';
  const word = sub ? 'SLBM' : 'ICBM';
  const cost = sub ? BALANCE.sub.slbmCost : BALANCE.silo.missileCost;
  const cap = sub ? BALANCE.sub.slbms : BALANCE.silo.magazine;
  const fill = (e: EntityView) => (e.detail?.loaded ?? 0) + (e.detail?.queued ?? 0);
  const room = launchers.filter((e) => fill(e) < cap);
  const into = room.filter(atPort).sort((a, b) => fill(a) - fill(b) || a.id - b.id);
  const n = Math.min(into.length, Math.floor(view.credits / cost));
  const why = !room.length ? 'MAGAZINE FULL' : !into.length ? 'SLBMS LOAD IN PORT · SAIL TO OUR COAST' : broke(view, cost);
  const buys = into.slice(0, Math.max(1, n));
  return {
    key: KEY.buy,
    label: `BUY ${word}${n > 1 ? ` ×${n}` : ''}`,
    cost: cost * Math.max(1, n),
    enabled: n > 0,
    why: n > 0 ? undefined : why,
    intent: (buys.length ? buys : launchers.slice(0, 1)).map((e): Intent => ({ type: 'buyMissile', siloId: e.id, count: 1 })),
  };
}

/** The silo among `silos` an ICBM buy goes into: the emptiest with room (null: all full). */
export function emptiestSilo(silos: readonly EntityView[]): EntityView | null {
  const fill = (s: EntityView) => (s.detail?.loaded ?? 0) + (s.detail?.queued ?? 0);
  const room = silos.filter((s) => fill(s) < BALANCE.silo.magazine);
  return room.length ? room.reduce((a, b) => (fill(b) < fill(a) ? b : a)) : null;
}

/** SAM sites (one or several selected), as one: `AMMO 7/12 · RELOAD 0:21` (the next round of any of them). */
export function samsStats(view: PlayerView, sams: readonly EntityView[]): string {
  const ammo = sams.reduce((n, s) => n + (s.detail?.ammo ?? s.extra), 0);
  let stats = `AMMO ${ammo}/${BALANCE.sam.ammo * sams.length}`;
  const reloads = sams.map((s) => s.detail?.reloadAt ?? 0).filter((r) => r > 0);
  if (reloads.length) stats += ` · RELOAD ${mmss(Math.min(...reloads) - view.tick)}`;
  return stats;
}

/**
 * The SAM chip's R: emissions control for every selected site at once (`SILENT ON`: no own radar,
 * half range, never gives itself away). The label is the state now (`SILENT MIXED` when the
 * selection disagrees); pressing sets every selected SAM to the other one.
 */
export function samSilentAction(sams: readonly EntityView[]): ChipAction {
  const silent = sams.filter((s) => s.detail?.silent).length;
  const quiet = silent !== sams.length;
  return {
    key: KEY.special,
    label: silent === 0 ? 'SILENT OFF' : silent === sams.length ? 'SILENT ON' : 'SILENT MIXED',
    enabled: sams.length > 0,
    tip: `SILENT: RADAR OFF · FIRES ONLY ON TRACKED TARGETS, ${Math.round(BALANCE.sam.silentRangeMult * 100)}% RANGE · NEVER GIVES ITSELF AWAY`,
    intent: sams.map((s): Intent => ({ type: 'samMode', samId: s.id, silent: quiet })),
  };
}

/** `SILENT · EXPOSED 0:21`: the selected SAMs' silence, as far as they agree, and the longest exposure left. */
export function samModeStats(view: PlayerView, sams: readonly EntityView[]): string {
  const parts: string[] = [];
  if (sams.length && sams.every((s) => s.detail?.silent)) parts.push('SILENT');
  const exposed = Math.max(0, ...sams.map((s) => (s.detail?.exposedUntil ?? 0) - view.tick));
  if (exposed > 0) parts.push(`EXPOSED ${mmss(exposed)}`);
  return parts.join(' · ');
}

/** The SAM among `sams` a bought interceptor goes into: the emptiest with room (null: all full). */
export function emptiestSam(sams: readonly EntityView[]): EntityView | null {
  const ammo = (s: EntityView) => s.detail?.ammo ?? s.extra;
  const room = sams.filter((s) => ammo(s) < BALANCE.sam.ammo);
  return room.length ? room.reduce((a, b) => (ammo(b) < ammo(a) ? b : a)) : null;
}

/** Auto-scramble toggle of an airbase or carrier (its fighters rise against raids by themselves). */
function autoAction(base: EntityView): ChipAction {
  const on = !!base.detail?.autoScramble;
  return { key: KEY.toggle, label: on ? 'AUTO ON' : 'AUTO OFF', enabled: true, intent: { type: 'autoScramble', baseId: base.id, on: !on } };
}

export function chipFor(
  view: PlayerView,
  sel: readonly EntityView[],
  cityName: (e: EntityView) => string | null,
  econ?: (e: EntityView) => CityIncome | null,
  st: ChipState = {},
): Chip | null {
  const own = sel.filter((e) => e.owner === view.me);
  if (!own.length) return null;
  const e = own[0];
  const d = e.detail ?? {};
  const t = view.tick;
  const many = own.length > 1 ? ` ×${own.length}` : '';
  const actions: ChipAction[] = [];
  let title = KIND_NAME[e.kind] ?? e.kind.toUpperCase();
  let stats = '';
  /** launchers of the selection still rippling: say so, and X stops them */
  const rippleStatus = (ls: readonly EntityView[]) => {
    const on = ls.filter((l) => st.rippling?.has(l.id)).map((l) => l.id);
    if (!on.length || !st.stopRipple) return;
    stats += ' · RIPPLE';
    const stop = st.stopRipple;
    actions.push({ key: KEY.cancel, label: 'STOP RIPPLE', enabled: true, run: () => stop(on) });
  };

  if (e.state === 'building' && e.kind === 'sub') {
    const c = subChip(view, own, e);
    return { title: title + many, stats: c.stats, actions: c.actions };
  }
  if (e.state === 'building') {
    const left = d.buildDoneTick !== undefined ? d.buildDoneTick - t : null;
    stats = left !== null ? `BUILDING · ${mmss(left)}` : 'BUILDING';
    // its keys, greyed, each saying when (a key pressed now answers instead of falling through)
    const ready = left !== null ? `READY IN ${mmss(left)}` : 'STILL BUILDING';
    const rel = BALANCE.phases.releaseTick - t;
    const later = (a: ChipAction) => (a.key === KEY.attack && !view.released && rel > (left ?? 0) ? `WEAPONS RELEASE IN ${mmss(rel)}` : ready);
    const done = chipFor(view, [{ ...e, state: 'active' }], cityName, econ)?.actions ?? [];
    for (const a of done) if (a.key !== KEY.cancel) actions.push({ key: a.key, label: a.label, enabled: false, why: later(a) });
    actions.push({ key: KEY.cancel, label: 'CANCEL', enabled: true, intent: { type: 'cancel', unitId: e.id } });
    return { title: title + many, stats, actions };
  }

  switch (e.kind) {
    case 'silo': {
      const silos = own.filter((s) => s.kind === 'silo' && s.state !== 'building');
      stats = silosStats(view, silos);
      // F buys one ICBM into each selected silo with room (as many as the credits cover, emptiest first)
      actions.push(buyMissiles(view, silos.length ? silos : [e]));
      const block = launchBlocker(view, silos);
      actions.push({ key: KEY.attack, label: view.released ? 'LAUNCH' : 'LOCKED', enabled: block === null, why: block ?? undefined, aim: 'launch' });
      rippleStatus(silos);
      break;
    }
    case 'sub': {
      const c = subChip(view, own, e);
      stats = c.stats;
      actions.push(...c.actions);
      rippleStatus(own.filter((x) => x.kind === 'sub'));
      break;
    }
    case 'carrier':
      // SAM rounds; its hit points only once it is damaged (constants are noise: the rings show reach)
      stats = `SAM ${d.ammo ?? 0}/${BALANCE.carrier.samAmmo}`;
      if ((d.hp ?? BALANCE.carrier.hp) < BALANCE.carrier.hp) stats = `HP ${d.hp ?? 0}/${BALANCE.carrier.hp} · ${stats}`;
      actions.push({ key: KEY.defend, label: 'PATROL', enabled: parkedAt(view, e.id, 'fighter') > 0, why: 'NO FIGHTER ON DECK', aim: 'patrol' });
      actions.push({
        key: KEY.buy,
        label: '+FIGHTER',
        cost: BALANCE.fighter.cost,
        enabled: view.credits >= BALANCE.fighter.cost,
        why: broke(view, BALANCE.fighter.cost),
        intent: { type: 'buildAircraft', baseId: e.id, kind: 'fighter' },
      });
      actions.push(autoAction(e));
      break;
    case 'airbase': {
      const h = hangarOf(view, e.id);
      const f = h.fighter.ready;
      const b = h.bomber.ready;
      const full = h.count >= BALANCE.airbase.hangar;
      stats = `FTR ${f}/${h.fighter.total} · BMR ${b}/${h.bomber.total}`;
      if (h.nextReady !== null) stats += ` · NEXT ${mmss(h.nextReady - t)}`;
      if (full) stats += ' · HANGAR FULL';
      const buy = (key: string, kind: 'fighter' | 'bomber'): ChipAction => {
        const cost = BALANCE[kind].cost;
        return {
          key,
          label: `+${kind.toUpperCase()}`,
          cost,
          enabled: !full && view.credits >= cost,
          why: full ? `HANGAR FULL · ${BALANCE.airbase.hangar} AIRCRAFT` : broke(view, cost),
          intent: { type: 'buildAircraft', baseId: e.id, kind },
        };
      };
      actions.push({
        key: KEY.attack,
        label: 'STRIKE',
        enabled: b > 0 && view.released,
        why: view.released ? noneReady(h.bomber, 'BOMBER', KEY.buy2, t) : `WEAPONS LOCKED · ${mmss(BALANCE.phases.releaseTick - t)}`,
        aim: 'strike',
      });
      actions.push({ key: KEY.defend, label: 'PATROL', enabled: f > 0, why: noneReady(h.fighter, 'FIGHTER', KEY.buy, t), aim: 'patrol' });
      actions.push({ key: KEY.special, label: 'SCOUT', enabled: f > 0, why: noneReady(h.fighter, 'FIGHTER', KEY.buy, t), aim: 'scout' });
      actions.push(buy(KEY.buy, 'fighter'), buy(KEY.buy2, 'bomber'));
      actions.push(autoAction(e));
      break;
    }
    case 'city': {
      const lvl = d.level ?? e.extra;
      title = cityName(e) ?? 'CITY';
      const inc = econ?.(e) ?? null;
      stats = `L${lvl}${inc ? ` · ${perMin(inc.perMin)}` : ''}`;
      const cost = cityUpgradeCost(lvl);
      // while an upgrade runs, the offer is taken: the button becomes its progress, then offers the next level
      if (d.upgradeDoneTick) {
        const left = d.upgradeDoneTick - t;
        actions.push({
          key: KEY.buy,
          label: `UPGRADING → L${lvl + 1} · ${mmss(left)}`,
          enabled: false,
          done: true,
          progress: Math.min(1, Math.max(0, 1 - left / BALANCE.city.upgradeTicks)),
          why: 'ALREADY UPGRADING',
        });
      } else if (lvl < BALANCE.city.maxLevel)
        actions.push({
          key: KEY.buy,
          label: `UPGRADE → L${lvl + 1}`,
          cost,
          note: inc ? perMin(inc.nextLevel) : undefined,
          tip: inc ? paybackTip(inc, BALANCE.matchTicks - t) : undefined,
          enabled: view.credits >= cost,
          why: broke(view, cost),
          intent: { type: 'upgradeCity', unitId: e.id },
        });
      actions.push(st.braceArms?.has(e.id) ? { key: KEY.special, label: 'BRACE ARMED', enabled: false, done: true } : braceAction(view, e));
      break;
    }
    case 'sam': {
      const sams = own.filter((x) => x.kind === 'sam' && x.state !== 'building');
      stats = samsStats(view, sams);
      const pri = samPriority(view, e);
      if (pri) stats += ` · ON ${pri.kind.toUpperCase()} T-${mmss(pri.launchTick + pri.flightTicks - t)}`;
      const next = nextPriority(view, sams);
      actions.push({ key: KEY.defend, label: 'TARGET', enabled: next.any, why: 'NO TRACKED MISSILE IN REACH', intent: next.intents });
      // the grid's order (Q E R F G Z X)
      actions.push(samSilentAction(sams));
      // F buys an interceptor into the emptiest selected SAM (straight into its magazine)
      const into = emptiestSam(sams);
      const cost = BALANCE.sam.roundCost;
      actions.push({
        key: KEY.buy,
        label: '+INTERCEPTOR',
        cost,
        enabled: !!into && view.credits >= cost,
        why: into ? broke(view, cost) : 'MAGAZINE FULL',
        intent: { type: 'buyMissile', siloId: (into ?? e).id, count: 1 },
      });
      const modes = samModeStats(view, sams);
      if (modes) stats += ` · ${modes}`;
      const set = sams.filter((x) => x.detail?.priorityId);
      if (set.length) actions.push({ key: KEY.cancel, label: 'CLEAR', enabled: true, intent: set.map((x) => ({ type: 'samPriority', samId: x.id, targetId: null })) });
      break;
    }
    // a radar's reach never changes (its ring shows it): nothing to read
    case 'fighter':
    case 'bomber':
      stats = aircraftStats(view, e);
      actions.push({ key: KEY.cancel, label: 'RECALL', enabled: d.phase !== 'parked', why: 'ALREADY HOME', intent: { type: 'airCmd', aircraftId: e.id, cmd: 'recall' } });
      break;
  }
  return { title: title + many, stats, actions };
}
